import { Language } from './../internationalText';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { useApolloClient } from '@apollo/client';
import { useState, useMemo } from 'react';
import {
  DownloadAnalysisTheme,
  DownloadAnalysisType,
  ScreenshotConfiguration,
} from '../../../graphql/projects/mutations/projects/useDownloadAnalysisGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import {
  AnalysisDownloadDataGq,
  AnalysisDownloadVariablesGq,
  analysisDownloadGqlQuery,
} from '../../../graphql/projects/queries/useGetAnalysisDownloadGq';
import { uuid4 } from '@sentry/utils';
import { AnalysisFilterState } from '../Filter/analysisFilterState';

export interface DownloadList {
  open: boolean;
  downloads: DownloadProgress[];
}

export interface DownloadProgress {
  id: string;
  name: string;
  state: 'downloading' | 'failed' | 'completed';
  downloadUrl?: string;
  zip?: boolean;
  type: DownloadAnalysisType;
  completedScreenshots: number;
  totalScreenshots: number;
}

export type DownloadBag = ReturnType<typeof useAnalysisDownload>;

export function useAnalysisDownload(
  project: ProjectGq,
  language: Language,
  theme: DownloadAnalysisTheme
) {
  const [downloadState, setDownloadState] = useState<DownloadList>({
    open: false,
    downloads: [],
  });
  const downloadMutation = projectsApiService.mutations.projects.useDownloadAnalysis();
  const apolloClient = useApolloClient();
  const handleDownload = (
    type: DownloadAnalysisType,
    name: string,
    configurations: ScreenshotConfiguration[]
  ) => {
    const id = uuid4();
    setDownloadState((prev) => ({
      ...prev,
      open: true,
      downloads: [
        {
          name,
          id,
          type,
          zip: type === 'screenshot' && configurations.length > 1,
          state: 'downloading',
          completedScreenshots: 0,
          totalScreenshots: configurations.length,
        },
        ...prev.downloads,
      ],
    }));

    downloadMutation({
      projectId: project.id,
      authenticationStorage: Object.keys(localStorage).map((key) => ({
        key,
        value: localStorage.getItem(key),
      })),
      type,
      theme: theme ?? 'voxel',
      screenshotConfigurations: configurations.map((conf): ScreenshotConfiguration => {
        // Change to the current language setting - it feels the most intuitive
        const modifiedAnalysisFilter = JSON.parse(conf.analysisFilter) as AnalysisFilterState;
        modifiedAnalysisFilter.language = language;
        modifiedAnalysisFilter.theme = theme ?? 'voxel';

        return {
          ...conf,
          analysisFilter: JSON.stringify(modifiedAnalysisFilter),
        };
      }),
    })
      .then(async (result) => {
        let downloadUrl = result.download.downloadUrl;
        let completedScreenshots = 0;
        if (downloadUrl) {
          setDownloadState((prev) => ({
            ...prev,
            downloads: prev.downloads.map((download) =>
              download.id === id ? { ...download, state: 'completed', downloadUrl } : download
            ),
          }));
        }
        while (!downloadUrl) {
          // Wait 250ms
          await new Promise((resolve, reject) => setTimeout(resolve, 250));

          // Get download again
          const refetchedDownload = await apolloClient.query<
            AnalysisDownloadDataGq,
            AnalysisDownloadVariablesGq
          >({
            query: analysisDownloadGqlQuery,
            variables: {
              command: {
                projectId: project.id,
                downloadId: result.download.id,
              },
            },
          });

          // Update progress
          const analysisDownload = refetchedDownload.data.projects.getAnalysisDownload.download;
          const newChange =
            !!analysisDownload.downloadUrl ||
            completedScreenshots !== analysisDownload.analysisProgress;

          if (newChange) {
            completedScreenshots = analysisDownload.analysisProgress;
            setDownloadState((prev) => {
              const nextDownloads = [...prev.downloads];

              // Find this download
              const index = prev.downloads.findIndex((x) => x.id === id);

              // Update it
              const downloadProgress = { ...nextDownloads[index] };
              downloadProgress.completedScreenshots = analysisDownload.analysisProgress;
              downloadProgress.downloadUrl = analysisDownload.downloadUrl;
              downloadProgress.state = !!analysisDownload.downloadUrl ? 'completed' : 'downloading';

              nextDownloads[index] = downloadProgress;

              return { ...prev, downloads: nextDownloads };
            });

            if (analysisDownload.downloadUrl) {
              break;
            }
          }
        }
      })
      .catch((error) => {
        console.error(error);
        setDownloadState((prev) => ({
          ...prev,
          downloads: prev.downloads.map((download) =>
            download.id === id ? { ...download, status: 'failed' } : download
          ),
        }));
      });

    // Return ID if it should be used in UI somehow
    return id;
  };

  // Track if anything is downloading
  const downloading = useMemo(
    () => downloadState.downloads.some((download) => download.state === 'downloading'),
    [downloadState]
  );

  return {
    handleDownload,
    setDownloadState,
    downloadState,
    downloading,
  };
}
